import { ChevronDown, ChevronUp, Trash2 } from 'lucide-solid';
import { createEffect, createMemo, createSignal, For, Show, type Accessor, type JSX } from 'solid-js';
import { RangePair } from '../components/RangePair';
import { Switch } from '../components/Switch';
import { BusySlot } from '../components/busy';
import {
  effectControlsFollowValues,
  effectPackageControls,
  formatParameterValue,
  syncReplacedParameter,
  type EffectPackageControl,
  type EffectPackageControlValue,
  type EffectPackageItem,
  type EffectPackageSliderDefinition,
  type EffectZoneNames,
} from '../lib/effectPackages';
import { TEST_IDS, testIdOf } from '../testIds';

export interface EffectPackageControlsProps {
  item: EffectPackageItem;
  values: Accessor<Record<string, EffectPackageControlValue>>;
  synced: Accessor<boolean>;
  disabled: Accessor<boolean>;
  idPrefix: string;
  fixtures?: Accessor<readonly { name?: string }[]>;
  zoneNames?: Accessor<EffectZoneNames>;
  fieldError?: Accessor<{ key: string; message: string } | undefined>;
  /**
   * This control's last move is sent and unanswered.
   *
   * The value itself already moved under the finger — that beat owes nothing to the network — so this
   * marks only the commit the controller still has to confirm.
   */
  committing?: (key: string) => boolean;
  onUpdate: (
    parameters: Record<string, EffectPackageControlValue>,
    gestureKey: string,
    final: boolean,
  ) => void;
}

function numeric(value: EffectPackageControlValue | undefined, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

/** One control's value read as a list, whatever it happens to be holding. */
function chosenList(value: EffectPackageControlValue | undefined): string[] {
  return Array.isArray(value) ? value : [];
}

/** One per-element control's list for one move. */
function chosenFor(value: EffectPackageControlValue | undefined, element: string): string[] {
  const map = value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : undefined;
  const entry = map?.[element];
  return Array.isArray(entry) ? entry as string[] : [];
}

/** Every move's list, so one move's edit is written back beside the others rather than over them. */
function chosenMap(value: EffectPackageControlValue | undefined): Record<string, string[]> {
  const map = value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : undefined;
  const next: Record<string, string[]> = {};
  for (const [key, entry] of Object.entries(map ?? {})) if (Array.isArray(entry)) next[key] = entry as string[];
  return next;
}

/** Every move's whole number, for the same reason. */
function countMap(value: EffectPackageControlValue | undefined): Record<string, number> {
  const map = value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : undefined;
  const next: Record<string, number> = {};
  for (const [key, entry] of Object.entries(map ?? {})) {
    if (typeof entry === 'number' && Number.isFinite(entry)) next[key] = entry;
  }
  return next;
}

/** One per-element whole number, or nothing when the map has not named that move. */
function countFor(value: EffectPackageControlValue | undefined, element: string): number | undefined {
  const map = value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : undefined;
  const entry = map?.[element];
  return typeof entry === 'number' && Number.isFinite(entry) ? entry : undefined;
}

/** The same list with one entry taken out if it was there, and put in if it was not. */
function toggled(list: readonly string[], value: string): string[] {
  return list.includes(value) ? list.filter((entry) => entry !== value) : [...list, value];
}

function ParameterSection(props: {
  name: string;
  collapsed: boolean;
  controls: Accessor<EffectPackageControl[]>;
  row: (control: EffectPackageControl) => JSX.Element;
}): JSX.Element {
  const [expanded, setExpanded] = createSignal(!props.collapsed);
  return (
    <section class="effect-package-section">
      <h3 class="effect-package-section-heading">
        <button type="button" aria-expanded={expanded()} onClick={() => setExpanded(!expanded())}>
          <span>{props.name}</span>
          <ChevronDown size={16} aria-hidden="true" />
        </button>
      </h3>
      <Show when={expanded()}>
        <div class="effect-package-section-rows">
          <For each={props.controls()}>{props.row}</For>
        </div>
      </Show>
    </section>
  );
}

/** The manifest-driven control rows shared by standalone packages and Audio reactive's instrument. */
export function EffectPackageControls(props: EffectPackageControlsProps): JSX.Element {
  const controlIdentities = new Map<string, { signature: string; control: EffectPackageControl }>();
  /**
   * Whether this thing's controls have to be re-derived when its VALUES move.
   *
   * Read first and on its own, so a package whose controls are all unconditional never makes
   * the list below depend on the values at all.
   */
  const conditional = createMemo(() => effectControlsFollowValues(props.item));
  const controls = createMemo<EffectPackageControl[]>(() => {
    const next = conditional()
      ? effectPackageControls(props.item, props.values(), props.zoneNames?.())
      : effectPackageControls(props.item, undefined, props.zoneNames?.());
    const live = new Set(next.map((control) => control.key));
    for (const key of [...controlIdentities.keys()]) if (!live.has(key)) controlIdentities.delete(key);
    return next.map((control) => {
      const signature = JSON.stringify(control);
      const kept = controlIdentities.get(control.key);
      if (kept?.signature === signature) return kept.control;
      controlIdentities.set(control.key, { signature, control });
      return control;
    });
  });
  const unnamedControls = createMemo(() => {
    const names = new Set((props.item.sections ?? []).map((section) => section.name));
    return controls().filter((control) => !control.section || !names.has(control.section));
  });
  const controlsForSection = (name: string): EffectPackageControl[] =>
    controls().filter((control) => control.section === name);

  /** Only the parameter named by sync.speed is taken over by the lock. */
  const beatLocked = createMemo(() => (props.synced() ? syncReplacedParameter(props.item) : undefined));
  const fixtureIndex = (key: string): number => numeric(
    props.values()[key], numeric(props.item.parameters[key]?.default, 0));
  const fixtureCollision = createMemo(() => {
    const fixtureA = props.item.parameters.fixtureA;
    const fixtureB = props.item.parameters.fixtureB;
    if (fixtureA?.type !== 'fixture' || fixtureB?.type !== 'fixture') return false;
    const a = fixtureIndex('fixtureA');
    const b = fixtureIndex('fixtureB');
    return Number.isInteger(a) && a === b;
  });
  function inert(...keys: string[]): boolean {
    return props.disabled() || keys.includes(beatLocked() ?? '');
  }

  function sliderRow(definition: EffectPackageSliderDefinition): JSX.Element {
    const value = (): number => numeric(props.values()[definition.key], definition.min);
    return (
      <label class="effect-package-parameter">
        <span>
          <strong>{definition.label}</strong>
          <Show when={definition.meaning}><small>{definition.meaning}</small></Show>
        </span>
        <output data-testid={testIdOf('controlValue', { prefix: props.idPrefix, key: definition.key })}>
          <BusySlot busy={props.committing?.(definition.key) === true} />
          {formatParameterValue(value(), definition.step)}
          <Show when={definition.unit}><span class="parameter-unit">{definition.unit}</span></Show>
        </output>
        <input
          type="range"
          min={definition.min}
          max={definition.max}
          step={definition.step}
          value={value()}
          disabled={inert(definition.key)}
          aria-label={definition.label}
          data-testid={testIdOf('control', { prefix: props.idPrefix, key: definition.key })}
          onInput={(event) => props.onUpdate(
            { [definition.key]: event.currentTarget.valueAsNumber }, definition.key, false,
          )}
          onChange={(event) => props.onUpdate(
            { [definition.key]: event.currentTarget.valueAsNumber }, definition.key, true,
          )}
        />
      </label>
    );
  }

  function controlRow(control: EffectPackageControl): JSX.Element {
    switch (control.kind) {
      case 'range-pair':
        return (
          <RangePair
            minDefinition={control.min}
            maxDefinition={control.max}
            minValue={numeric(props.values()[control.min.key], control.min.min)}
            maxValue={numeric(props.values()[control.max.key], control.max.max)}
            class="effect-package-parameter"
            testIdPrefix={props.idPrefix}
            disabled={inert(control.min.key, control.max.key)}
            committing={props.committing}
            formatValue={(value) => formatParameterValue(value, control.min.step)}
            onChange={(change, final) => props.onUpdate(change.parameters, control.key, final)}
          />
        );
      case 'slider':
      case 'stepper':
        return sliderRow(control.definition);
      case 'segmented':
        return (
          <div class="effect-package-parameter effect-package-choice">
            <span>
              <strong>{control.label}</strong>
              <Show when={control.meaning}><small>{control.meaning}</small></Show>
            </span>
            <div class="effect-package-segmented" role="radiogroup" aria-label={control.label}>
              <For each={control.values}>
                {(value, index) => (
                  <button
                    type="button"
                    role="radio"
                    classList={{ selected: props.values()[control.key] === value }}
                    aria-checked={props.values()[control.key] === value}
                    disabled={inert(control.key)}
                    data-testid={testIdOf('controlOption', { prefix: props.idPrefix, key: control.key, value })}
                    onClick={() => props.onUpdate({ [control.key]: value }, control.key, true)}
                  >{control.labels?.[index()] ?? value}</button>
                )}
              </For>
            </div>
          </div>
        );
      case 'select':
        return (
          <label class="effect-package-parameter">
            <span>
              <strong>{control.label}</strong>
              <Show when={control.meaning}><small>{control.meaning}</small></Show>
            </span>
            <select
              value={String(props.values()[control.key] ?? '')}
              disabled={inert(control.key)}
              aria-label={control.label}
              data-testid={testIdOf('control', { prefix: props.idPrefix, key: control.key })}
              onChange={(event) => props.onUpdate({ [control.key]: event.currentTarget.value }, control.key, true)}
            >
              <For each={control.values}>
                {(value, index) => <option value={value}>{control.labels?.[index()] ?? value}</option>}
              </For>
            </select>
          </label>
        );
      case 'fixture': {
        let picker!: HTMLSelectElement;
        createEffect(() => {
          props.fixtures?.();
          const value = String(fixtureIndex(control.key));
          if (picker && picker.value !== value) picker.value = value;
        });
        return (
          <Show when={(props.fixtures?.()?.length ?? 0) > 0}>
            <label class="effect-package-parameter">
              <span><strong>{control.label}</strong></span>
              <select
                ref={picker}
                value={String(fixtureIndex(control.key))}
                data-fixture-collision={fixtureCollision() && (control.key === 'fixtureA' || control.key === 'fixtureB') ? 'true' : undefined}
                disabled={inert(control.key)}
                aria-label={control.label}
                data-testid={testIdOf('control', { prefix: props.idPrefix, key: control.key })}
                onChange={(event) => props.onUpdate({ [control.key]: Number(event.currentTarget.value) }, control.key, true)}
              >
                <For each={props.fixtures?.() ?? []}>
                  {(fixture, index) => <option value={index()}>{fixture.name}</option>}
                </For>
              </select>
            </label>
          </Show>
        );
      }
      case 'switch':
        return (
          <div class="effect-package-parameter effect-package-switch">
            <span>
              <strong>{control.label}</strong>
              <Show when={control.meaning}><small>{control.meaning}</small></Show>
            </span>
            <Switch
              checked={props.values()[control.key] === true}
              busy={props.committing?.(control.key) === true}
              disabled={inert(control.key)}
              label={control.label}
              testId={testIdOf('control', { prefix: props.idPrefix, key: control.key })}
              onChange={(checked) => props.onUpdate({ [control.key]: checked }, control.key, true)}
            />
          </div>
        );
      case 'checks':
        return (
          <div class="effect-package-parameter effect-package-choice">
            <span>
              <strong>{control.label}</strong>
              <Show when={control.meaning}><small>{control.meaning}</small></Show>
            </span>
            {checkGroup(
              control.label,
              control.key,
              control.values,
              control.labels,
              () => chosenList(props.values()[control.key]),
              (next) => props.onUpdate({ [control.key]: next }, control.key, true),
              inert(control.key),
            )}
          </div>
        );
      case 'sequence':
        return sequenceRow(control);
      case 'element-checks':
        return (
          <div class="effect-package-parameter effect-package-elements">
            <span>
              <strong>{control.label}</strong>
              <Show when={control.meaning}><small>{control.meaning}</small></Show>
            </span>
            <div class="effect-package-element-rows">
            <For each={control.elements}>
              {(element) => (
                <div class="effect-package-element">
                  <span>{element.label}</span>
                  {checkGroup(
                    `${control.label} · ${element.label}`,
                    `${control.key}-${element.identifier}`,
                    control.values,
                    control.labels,
                    () => chosenFor(props.values()[control.key], element.identifier),
                    (next) => props.onUpdate(
                      {
                        [control.key]: {
                          ...chosenMap(props.values()[control.key]),
                          [element.identifier]: next,
                        },
                      },
                      control.key,
                      true,
                    ),
                    inert(control.key),
                  )}
                </div>
              )}
            </For>
            </div>
          </div>
        );
      case 'element-counts':
        return (
          <div class="effect-package-parameter effect-package-elements">
            <span>
              <strong>{control.label}</strong>
              <Show when={control.meaning}><small>{control.meaning}</small></Show>
            </span>
            <div class="effect-package-element-rows">
            <For each={control.elements}>
              {(element) => (
                <label class="effect-package-element">
                  <span>{element.label}</span>
                  <input
                    type="number"
                    {...(control.min != null ? { min: control.min } : {})}
                    {...(control.max != null ? { max: control.max } : {})}
                    step="1"
                    value={countFor(props.values()[control.key], element.identifier) ?? ''}
                    disabled={inert(control.key)}
                    aria-label={`${control.label} · ${element.label}`}
                    data-testid={testIdOf('controlOption', { prefix: props.idPrefix, key: control.key, value: element.identifier })}
                    onChange={(event) => props.onUpdate(
                      {
                        [control.key]: {
                          ...countMap(props.values()[control.key]),
                          [element.identifier]: event.currentTarget.valueAsNumber,
                        },
                      },
                      control.key,
                      true,
                    )}
                  />
                </label>
              )}
            </For>
            </div>
          </div>
        );
    }
  }

  /**
   * A set of entries chosen in no order.
   *
   * Buttons rather than checkboxes, so it reads as the segmented control beside it and the row keeps one
   * shape; `role="checkbox"` is what says any number of them may be on. No entry is refused here — the
   * renderer is the authority on what it will accept, and the console does not pre-validate and hide a
   * refusal (B17); an empty list comes back as the renderer's own words at the field.
   */
  function checkGroup(
    label: string,
    testKey: string,
    values: readonly string[],
    labels: readonly string[],
    chosen: () => string[],
    onChange: (next: string[]) => void,
    disabled: boolean,
  ): JSX.Element {
    return (
      <div class="effect-package-segmented" role="group" aria-label={label}>
        <For each={values}>
          {(value, index) => (
            <button
              type="button"
              role="checkbox"
              classList={{ selected: chosen().includes(value) }}
              aria-checked={chosen().includes(value)}
              disabled={disabled}
              data-testid={testIdOf('controlOption', { prefix: props.idPrefix, key: testKey, value })}
              onClick={() => onChange(toggled(chosen(), value))}
            >{labels[index()] ?? value}</button>
          )}
        </For>
      </div>
    );
  }

  /**
   * An ordered run of entries, in which repeating an entry is the point.
   *
   * A set of ticks cannot express this: the choreography IS the order, and the same zone may legitimately
   * appear twice. So the operator builds a list — append from a menu, move an entry, take one out — using
   * the same four words the stack's own layer rows already use, and no new ones.
   */
  function sequenceRow(control: Extract<EffectPackageControl, { kind: 'sequence' }>): JSX.Element {
    const entries = (): string[] => chosenList(props.values()[control.key]);
    const send = (next: string[]): void => props.onUpdate({ [control.key]: next }, control.key, true);
    function move(index: number, direction: -1 | 1): void {
      const next = [...entries()];
      const swap = index + direction;
      if (swap < 0 || swap >= next.length) return;
      [next[index], next[swap]] = [next[swap], next[index]];
      send(next);
    }
    let appender!: HTMLSelectElement;
    return (
      <div class="effect-package-parameter effect-package-sequence">
        <span>
          <strong>{control.label}</strong>
          <Show when={control.meaning}><small>{control.meaning}</small></Show>
        </span>
        {/* Bounded, and scrolls inside itself: an operator may append the same entry as many times as
            the choreography calls for, and a list that grew the card would push Start off the screen. */}
        <ol class="effect-package-sequence-items">
          <For each={entries()}>
            {(entry, index) => (
              <li data-testid={testIdOf('controlItem', { prefix: props.idPrefix, key: control.key, index: index() })}>
                <span>{control.labels[control.values.indexOf(entry)] ?? entry}</span>
                <button
                  type="button"
                  aria-label="Move up"
                  disabled={inert(control.key) || index() === 0}
                  data-testid={testIdOf('controlItemUp', { prefix: props.idPrefix, key: control.key, index: index() })}
                  onClick={() => move(index(), -1)}
                ><ChevronUp size={16} /></button>
                <button
                  type="button"
                  aria-label="Move down"
                  disabled={inert(control.key) || index() === entries().length - 1}
                  data-testid={testIdOf('controlItemDown', { prefix: props.idPrefix, key: control.key, index: index() })}
                  onClick={() => move(index(), 1)}
                ><ChevronDown size={16} /></button>
                <button
                  type="button"
                  aria-label="Remove"
                  disabled={inert(control.key)}
                  data-testid={testIdOf('controlItemRemove', { prefix: props.idPrefix, key: control.key, index: index() })}
                  onClick={() => send(entries().filter((_, position) => position !== index()))}
                ><Trash2 size={16} /></button>
              </li>
            )}
          </For>
        </ol>
        <select
          ref={appender}
          aria-label="Add"
          disabled={inert(control.key)}
          data-testid={testIdOf('controlItemAdd', { prefix: props.idPrefix, key: control.key })}
          value=""
          onChange={(event) => {
            const value = event.currentTarget.value;
            // The picker is an action, not a state: it goes back to resting the moment it has been used,
            // which is also what lets the same entry be appended twice in a row.
            appender.value = '';
            if (value) send([...entries(), value]);
          }}
        >
          <option value="" />
          <For each={control.values}>
            {(value, index) => <option value={value}>{control.labels[index()] ?? value}</option>}
          </For>
        </select>
      </div>
    );
  }

  function errorFor(control: EffectPackageControl): string | undefined {
    const current = props.fieldError?.();
    if (!current) return undefined;
    const keys = control.kind === 'range-pair' ? [control.min.key, control.max.key] : [control.key];
    return keys.includes(current.key) ? current.message : undefined;
  }

  function renderedRow(control: EffectPackageControl): JSX.Element {
    return (
      <div class="effect-package-row" data-testid={testIdOf('controlRow', { prefix: props.idPrefix, key: control.key })}>
        {controlRow(control)}
        <Show when={errorFor(control)}>
          {(message) => (
            <p class="form-error" data-testid={testIdOf('controlError', { prefix: props.idPrefix, key: control.key })}>{message()}</p>
          )}
        </Show>
      </div>
    );
  }

  return (
    <div class="effect-package-parameters" data-testid={TEST_IDS.effectPackageParameters}>
      <Show when={props.item.sections?.length} fallback={<For each={controls()}>{renderedRow}</For>}>
        <For each={unnamedControls()}>{renderedRow}</For>
        <For each={props.item.sections ?? []}>
          {(section) => (
            <Show when={controlsForSection(section.name).length}>
              <ParameterSection
                name={section.name}
                collapsed={section.collapsed}
                controls={() => controlsForSection(section.name)}
                row={renderedRow}
              />
            </Show>
          )}
        </For>
      </Show>
    </div>
  );
}
